<template>
  <li class="tree__sub" :class="{ active: table._.selected }">
    <a
      class="tree__sub-link"
      :class="[
        { 'tree__sub-link--empty': table.name === '' },
        table.color
          ? `tree__sub-link--colored background-color--${table.color}`
          : '',
      ]"
      :title="table.name"
      :href="resolveTableHref(database, table)"
      @mousedown.prevent
      @click.prevent="selectTable(database, table)"
      @contextmenu.prevent.stop="openContextMenu($event)"
    >
      <i
        v-if="table.data_sync"
        v-tooltip:[syncTooltipOptions]="
          `${$t('sidebarItem.lastSynced')}: ${lastSyncedDate}`
        "
        class="iconoir-data-transfer-down"
      ></i>
      <Editable
        ref="rename"
        :value="table.name"
        @change="renameTable(database, table, $event)"
      ></Editable>
    </a>
    <span v-if="openRowCount" class="tree__sub-count">{{ openRowCount }}</span>

    <a
      v-if="showOptions"
      v-show="!database._.loading"
      class="tree__options"
      @click="$refs.context.toggle($event.currentTarget, 'bottom', 'right', 0)"
      @mousedown.stop
    >
      <i class="baserow-icon-more-vertical"></i>
    </a>

    <Context ref="context" overflow-scroll max-height-if-outside-viewport>
      <div class="context__menu-title">{{ table.name }} ({{ table.id }})</div>
      <ul class="context__menu">
        <li
          v-for="(component, index) in additionalContextComponents"
          :key="index"
          class="context__menu-item"
          @click="$refs.context.hide()"
        >
          <component
            :is="component"
            :table="table"
            :database="database"
          ></component>
        </li>
        <li
          v-if="
            $hasPermission(
              'database.table.run_export',
              table,
              database.workspace.id
            )
          "
          class="context__menu-item"
        >
          <a class="context__menu-item-link" @click="exportTable()">
            <i class="context__menu-item-icon iconoir-share-ios"></i>
            {{ $t('sidebarItem.exportTable') }}
          </a>
        </li>
        <li
          v-if="
            $hasPermission(
              'database.table.run_export',
              table,
              database.workspace.id
            )
          "
          class="context__menu-item"
        >
          <!-- Origem Flow: relatório de tarefas por período (PDF/Excel). -->
          <a class="context__menu-item-link" @click="openReportModal()">
            <i class="context__menu-item-icon iconoir-doc-star"></i>
            {{ $t('sidebarItem.generateReport') }}
          </a>
        </li>
        <li class="context__menu-item">
          <!-- Origem Flow: conversa (áudio, reunião, texto) vira tarefas propostas. -->
          <a class="context__menu-item-link" @click="openConversationModal()">
            <i class="context__menu-item-icon iconoir-sparks"></i>
            {{ $t('sidebarItem.conversationToTasks') }}
          </a>
        </li>
        <li class="context__menu-item">
          <!-- Origem Flow: relatório semanal/mensal por link para o cliente. -->
          <a class="context__menu-item-link" @click="openDigestModal()">
            <i class="context__menu-item-icon iconoir-send-diagonal"></i>
            {{ $t('sidebarItem.clientDigest') }}
          </a>
        </li>
        <li class="context__menu-item">
          <!-- Origem Flow: docs do hub vinculados a esta tabela. -->
          <a class="context__menu-item-link" @click="openLinkedDocuments()">
            <i class="context__menu-item-icon iconoir-page"></i>
            {{ $t('sidebarItem.linkedDocuments') }}
          </a>
        </li>
        <li class="context__menu-item">
          <!-- Origem Flow: quadros brancos vinculados a esta tabela. -->
          <a class="context__menu-item-link" @click="openLinkedWhiteboards()">
            <i class="context__menu-item-icon iconoir-design-pencil"></i>
            {{ $t('sidebarItem.linkedWhiteboards') }}
          </a>
        </li>
        <li
          v-if="
            $hasPermission(
              'database.table.create_webhook',
              table,
              database.workspace.id
            )
          "
          class="context__menu-item"
        >
          <a class="context__menu-item-link" @click="openWebhookModal()">
            <i class="context__menu-item-icon iconoir-globe"></i>
            {{ $t('sidebarItem.webhooks') }}
          </a>
        </li>
        <li
          v-if="
            table.data_sync &&
            $hasPermission(
              'database.data_sync.sync_table',
              table,
              database.workspace.id
            )
          "
          class="context__menu-item"
        >
          <a class="context__menu-item-link" @click="openSyncModal()">
            <i class="context__menu-item-icon iconoir-data-transfer-down"></i>
            {{ $t('sidebarItem.sync') }}
            <div v-if="dataSyncDeactivated" class="deactivated-label">
              <i class="iconoir-lock"></i>
            </div>
          </a>
          <component
            :is="dataSyncDeactivatedClickModal[0]"
            v-if="dataSyncDeactivatedClickModal !== null"
            ref="deactivatedDataSyncClickModal"
            v-bind="dataSyncDeactivatedClickModal[1]"
            :workspace="database.workspace"
            :name="dataSyncType.getName()"
          ></component>
        </li>
        <li
          v-if="
            table.data_sync &&
            $hasPermission(
              'database.table.update',
              table,
              database.workspace.id
            ) &&
            $hasPermission(
              'database.data_sync.get',
              table,
              database.workspace.id
            )
          "
          class="context__menu-item"
        >
          <a
            class="context__menu-item-link"
            @click="openConfigureDataSyncModal()"
          >
            <i class="context__menu-item-icon iconoir-settings"></i>
            {{ $t('sidebarItem.updateSyncConfig') }}
            <div v-if="dataSyncDeactivated" class="deactivated-label">
              <i class="iconoir-lock"></i>
            </div>
          </a>
          <ConfigureDataSyncModal
            ref="configureDataSyncModal"
            :database="database"
            :table="table"
          ></ConfigureDataSyncModal>
        </li>
        <li
          v-if="
            $hasPermission(
              'database.table.update',
              table,
              database.workspace.id
            )
          "
          class="context__menu-item"
        >
          <a class="context__menu-item-link" @click="enableRename()">
            <i class="context__menu-item-icon iconoir-edit-pencil"></i>
            {{ $t('action.rename') }}
          </a>
        </li>
        <li
          v-if="
            $hasPermission(
              'database.table.update',
              table,
              database.workspace.id
            )
          "
          class="context__menu-item"
        >
          <!-- Origem Flow: cor de fundo da tabela na árvore do sidebar,
               sorteada na criação e editável aqui. -->
          <a class="context__menu-item-link" @click="openColorContext($event)">
            <i class="context__menu-item-icon iconoir-palette"></i>
            {{ $t('sidebarItem.changeColor') }}
          </a>
          <ColorSelectContext
            ref="colorContext"
            @selected="updateColor($event)"
          ></ColorSelectContext>
        </li>
        <li
          v-if="
            $hasPermission(
              'database.table.update',
              table,
              database.workspace.id
            )
          "
          class="context__menu-item"
        >
          <!-- Origem Flow: arquivar projeto encerrado (some da árvore, fica
               na seção "Arquivadas" e pode voltar quando quiser). -->
          <a class="context__menu-item-link" @click="toggleArchived()">
            <i
              class="context__menu-item-icon"
              :class="table.archived ? 'iconoir-undo' : 'iconoir-archive'"
            ></i>
            {{
              table.archived
                ? $t('sidebarItem.unarchive')
                : $t('sidebarItem.archive')
            }}
          </a>
        </li>
        <li
          v-if="
            $hasPermission(
              'database.table.duplicate',
              table,
              database.workspace.id
            )
          "
          class="context__menu-item"
        >
          <SidebarDuplicateTableContextItem
            :database="database"
            :table="table"
            :disabled="deleteLoading"
            @click="$refs.context.hide()"
          ></SidebarDuplicateTableContextItem>
        </li>
        <li
          v-if="
            $hasPermission(
              'database.table.delete',
              table,
              database.workspace.id
            )
          "
          class="context__menu-item"
        >
          <a
            class="context__menu-item-link context__menu-item-link--delete"
            :class="{ 'context__menu-item-link--loading': deleteLoading }"
            @click="deleteTable()"
          >
            <i class="context__menu-item-icon iconoir-bin"></i>
            {{ $t('action.delete') }}
          </a>
        </li>
      </ul>
      <ExportTableModal
        ref="exportTableModal"
        :database="database"
        :table="table"
      />
      <WebhookModal ref="webhookModal" :database="database" :table="table" />
      <ClientDigestModal
        ref="digestModal"
        :database="database"
        :table="table"
      ></ClientDigestModal>
      <TableReportModal
        ref="reportModal"
        :database="database"
        :table="table"
      />
      <SyncTableModal ref="syncModal" :table="table"></SyncTableModal>
    </Context>
  </li>
</template>

<script>
import { notifyIf } from '@baserow/modules/core/utils/error'
import { getHumanPeriodAgoCount } from '@baserow/modules/core/utils/date'
import ExportTableModal from '@baserow/modules/database/components/export/ExportTableModal'
import WebhookModal from '@baserow/modules/database/components/webhook/WebhookModal'
import SidebarDuplicateTableContextItem from '@baserow/modules/database/components/sidebar/table/SidebarDuplicateTableContextItem'
import ColorSelectContext from '@baserow/modules/core/components/ColorSelectContext'
import TableReportModal from '@baserow/modules/database/components/table/TableReportModal'
import ClientDigestModal from '@baserow/modules/database/components/digest/ClientDigestModal'
import SyncTableModal from '@baserow/modules/database/components/dataSync/SyncTableModal'
import ConfigureDataSyncModal from '@baserow/modules/database/components/dataSync/ConfigureDataSyncModal.vue'
import { pageFinished } from '@baserow/modules/core/utils/routing'
import { nextTick, useNuxtApp } from '#imports'

export default {
  name: 'SidebarItem',
  components: {
    ConfigureDataSyncModal,
    ExportTableModal,
    WebhookModal,
    SyncTableModal,
    SidebarDuplicateTableContextItem,
    ColorSelectContext,
    TableReportModal,
    ClientDigestModal,
  },
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const nuxtApp = useNuxtApp()
    return { nuxtApp }
  },
  data() {
    return {
      deleteLoading: false,
    }
  },
  computed: {
    /**
     * Badge de contagem estilo ClickUp: linhas "abertas" da tabela
     * (tableCounts store). Zero fica oculto, como no ClickUp.
     */
    openRowCount() {
      return this.$store.getters['tableCounts/getOpenCount'](this.table.id)
    },
    showOptions() {
      return (
        this.additionalContextComponents.length > 0 ||
        this.$hasPermission(
          'database.table.run_export',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.create_webhook',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.update',
          this.table,
          this.database.workspace.id
        ) ||
        this.$hasPermission(
          'database.table.duplicate',
          this.table,
          this.database.workspace.id
        )
      )
    },
    additionalContextComponents() {
      return Object.values(this.$registry.getAll('plugin'))
        .reduce((components, plugin) => {
          return components.concat(
            plugin.getAdditionalApplicationChildContextComponents(
              this.database.workspace,
              this.database,
              this.table
            )
          )
        }, [])
        .filter((component) => component !== null)
    },
    syncTooltipOptions() {
      return {
        contentClasses: ['tooltip__content--align-right'],
      }
    },
    lastSyncedDate() {
      if (!this.table.data_sync || !this.table.data_sync.last_sync) {
        return this.$t('sidebarItem.notSynced')
      }
      const { period, count } = getHumanPeriodAgoCount(
        this.table.data_sync.last_sync
      )
      return this.$t(`datetime.${period}Ago`, { count })
    },
    dataSyncType() {
      return this.$registry.get('dataSync', this.table.data_sync.type)
    },
    dataSyncDeactivated() {
      return this.dataSyncType.isDeactivated(this.database.workspace.id)
    },
    dataSyncDeactivatedClickModal() {
      return this.dataSyncType.getDeactivatedClickModal()
    },
  },
  methods: {
    /**
     * Origem Flow: botão direito no nome da tabela abre o mesmo menu do
     * "⋮" (contexto de opções), em vez do menu nativo do navegador.
     */
    openContextMenu(event) {
      if (!this.showOptions || this.database._.loading) {
        return
      }
      this.$refs.context.toggle(event.currentTarget, 'bottom', 'right', 0)
    },
    setLoading(database, value) {
      this.$store.dispatch('application/setItemLoading', {
        application: database,
        value,
      })
    },
    async selectTable(database, table) {
      if (table._.selected) {
        return
      }

      this.setLoading(database, true)

      try {
        // Vue Router 4: push() resolves to undefined on success, or a
        // NavigationFailure on failure (e.g. duplicate navigation).
        const failure = await this.$router.push({
          name: 'database-table',
          params: {
            databaseId: database.id,
            tableId: table.id,
          },
        })
        if (failure === undefined) {
          await pageFinished(this.nuxtApp)
          await nextTick()
        }
      } finally {
        this.setLoading(database, false)
      }
    },
    exportTable() {
      this.$refs.context.hide()
      this.$refs.exportTableModal.show()
    },
    openWebhookModal() {
      this.$refs.context.hide()
      this.$refs.webhookModal.show()
    },
    openReportModal() {
      this.$refs.context.hide()
      this.$refs.reportModal.show()
    },
    openDigestModal() {
      this.$refs.context.hide()
      this.$refs.digestModal.show()
    },
    openConversationModal() {
      this.$refs.context.hide()
      window.dispatchEvent(
        new CustomEvent('origem-conversation-open', {
          detail: { tableId: this.table.id },
        })
      )
    },
    openLinkedDocuments() {
      this.$refs.context.hide()
      this.$router.push({
        name: 'workspace-docs',
        params: { workspaceId: this.database.workspace.id },
        query: { table: this.table.id },
      })
    },
    openLinkedWhiteboards() {
      this.$refs.context.hide()
      this.$router.push({
        name: 'workspace-whiteboards',
        params: { workspaceId: this.database.workspace.id },
        query: { table: this.table.id },
      })
    },
    openSyncModal() {
      if (this.dataSyncDeactivated) {
        this.$refs.deactivatedDataSyncClickModal.show()
      } else {
        this.$refs.context.hide()
        this.$refs.syncModal.show()
      }
    },
    openConfigureDataSyncModal() {
      if (this.dataSyncDeactivated) {
        this.$refs.deactivatedDataSyncClickModal.show()
      } else {
        this.$refs.context.hide()
        this.$refs.configureDataSyncModal.show()
      }
    },
    enableRename() {
      this.$refs.context.hide()
      this.$refs.rename.edit()
    },
    openColorContext(event) {
      this.$refs.colorContext.setActive(this.table.color)
      this.$refs.colorContext.toggle(event.currentTarget, 'bottom', 'left', 4)
    },
    async updateColor(color) {
      try {
        await this.$store.dispatch('table/update', {
          database: this.database,
          table: this.table,
          values: { color },
        })
      } catch (error) {
        notifyIf(error, 'table')
      }
    },
    async toggleArchived() {
      this.$refs.context.hide()
      try {
        await this.$store.dispatch('table/update', {
          database: this.database,
          table: this.table,
          values: { archived: !this.table.archived },
        })
      } catch (error) {
        notifyIf(error, 'table')
      }
    },
    async renameTable(database, table, event) {
      this.setLoading(database, true)

      try {
        await this.$store.dispatch('table/update', {
          database,
          table,
          values: {
            name: event.value,
          },
        })
      } catch (error) {
        this.$refs.rename.set(event.oldValue)
        notifyIf(error, 'table')
      }

      this.setLoading(database, false)
    },
    async deleteTable() {
      this.deleteLoading = true

      try {
        await this.$store.dispatch('table/delete', {
          database: this.database,
          table: this.table,
        })
        await this.$store.dispatch('toast/restore', {
          trash_item_type: 'table',
          trash_item_id: this.table.id,
        })
      } catch (error) {
        notifyIf(error, 'table')
      }

      this.deleteLoading = false
    },
    resolveTableHref(database, table) {
      const props = this.$router.resolve({
        name: 'database-table',
        params: {
          databaseId: database.id,
          tableId: table.id,
        },
      })

      return props.href
    },
  },
}
</script>
